import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LoggerService } from '../services/logger.service';
import { UserStorageService } from '../services/user-storage.service';
import { TourDefinition, TourFactory, TourStep } from './tour.model';

interface PersistedTour {
  tourId: string;
  stepIndex: number;
}

const STORAGE_KEY = 'active_tour';

/**
 * Runs guided tours (see tour.model.ts); TourOverlayComponent renders them.
 *
 * - Next/Back navigate to the target step's `route`, if it has one.
 * - After every navigation, the current step's `advanceWhenUrl` is checked so
 *   steps the user completes in the real UI (e.g. saving a form) advance by
 *   themselves.
 * - Progress is persisted per user, so a reload (or an OTA update restart)
 *   doesn't lose the tour. A resumed tour starts *minimized*: the app may have
 *   been opened somewhere specific (a scanned tag link, say), so the tour
 *   doesn't navigate until the user taps Continue.
 */
@Injectable({ providedIn: 'root' })
export class TourService {
  private router = inject(Router);
  private userStorage = inject(UserStorageService);
  private log = inject(LoggerService).getLogger('tour');

  private readonly tour = signal<TourDefinition | null>(null);
  private readonly index = signal(0);
  private readonly factories = new Map<string, TourFactory>();

  /** Collapsed to a "Continue tour" pill so the user can look around freely. */
  readonly minimized = signal(false);

  readonly active = computed(() => this.tour() !== null);
  readonly tourId = computed(() => this.tour()?.id ?? null);
  readonly stepIndex = this.index.asReadonly();
  readonly stepCount = computed(() => this.tour()?.steps.length ?? 0);
  readonly step = computed<TourStep | null>(() => this.tour()?.steps[this.index()] ?? null);
  readonly isFirst = computed(() => this.index() === 0);
  readonly isLast = computed(() => this.index() === this.stepCount() - 1);

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe((e) => this.checkAutoAdvance(e.urlAfterRedirects));
  }

  /**
   * Registers how to rebuild tours whose id starts with `prefix`, so a
   * persisted tour can be resumed with {@link resume}.
   */
  registerFactory(prefix: string, factory: TourFactory): void {
    this.factories.set(prefix, factory);
  }

  /** Starts (or restarts) a tour at `stepIndex` and opens that step's route. */
  async start(tour: TourDefinition, stepIndex = 0): Promise<void> {
    this.log.info(`Starting tour '${tour.id}' at step ${stepIndex}`);
    this.tour.set(tour);
    this.minimized.set(false);
    await this.goTo(stepIndex);
  }

  /** Resumes a persisted tour, minimized and without navigating. */
  async resume(): Promise<boolean> {
    if (this.tour()) return true;
    const saved = this.userStorage.getTypedItem<PersistedTour>(STORAGE_KEY);
    if (!saved?.tourId) return false;

    const factory = [...this.factories.entries()].find(([prefix]) =>
      saved.tourId.startsWith(prefix),
    )?.[1];
    let tour: TourDefinition | null = null;
    try {
      tour = factory ? await factory(saved.tourId) : null;
    } catch (err) {
      // Transient (e.g. database not ready): keep the saved progress for next time.
      this.log.warn(`Couldn't resume tour '${saved.tourId}':`, err);
      return false;
    }
    if (!tour) {
      this.log.debug(`Dropping persisted tour '${saved.tourId}': can't rebuild it`);
      this.userStorage.removeItem(STORAGE_KEY);
      return false;
    }

    this.tour.set(tour);
    this.index.set(clamp(saved.stepIndex, 0, tour.steps.length - 1));
    this.minimized.set(true);
    this.log.info(`Resumed tour '${tour.id}' at step ${this.index()} (minimized)`);
    return true;
  }

  async next(): Promise<void> {
    if (!this.tour()) return;
    if (this.isLast()) {
      await this.finish();
      return;
    }
    await this.goTo(this.index() + 1);
  }

  async back(): Promise<void> {
    if (!this.tour() || this.isFirst()) return;
    await this.goTo(this.index() - 1);
  }

  /** Re-opens a minimized tour on its current step (navigating to the step's route). */
  async restore(): Promise<void> {
    if (!this.tour()) return;
    this.minimized.set(false);
    await this.goTo(this.index());
  }

  minimize(): void {
    this.minimized.set(true);
  }

  /** Closes the tour early. */
  end(): void {
    if (!this.tour()) return;
    this.log.info(`Tour '${this.tourId()}' closed at step ${this.index()}`);
    this.clear();
  }

  private async finish(): Promise<void> {
    const tour = this.tour();
    this.log.info(`Tour '${tour?.id}' finished`);
    this.clear();
    await tour?.onFinish?.();
  }

  private clear(): void {
    this.tour.set(null);
    this.index.set(0);
    this.minimized.set(false);
    this.userStorage.removeItem(STORAGE_KEY);
  }

  private async goTo(stepIndex: number): Promise<void> {
    const tour = this.tour();
    if (!tour) return;
    const index = clamp(stepIndex, 0, tour.steps.length - 1);
    this.index.set(index);
    this.userStorage.setTypedItem<PersistedTour>(STORAGE_KEY, { tourId: tour.id, stepIndex: index });

    const route = tour.steps[index].route;
    if (route) {
      const target = this.router.serializeUrl(this.router.createUrlTree(route));
      if (this.router.url !== target) {
        await this.router.navigateByUrl(target);
      }
    }
  }

  private checkAutoAdvance(url: string): void {
    const step = this.step();
    if (!step?.advanceWhenUrl || !step.advanceWhenUrl.test(url)) return;
    this.log.debug(`Step '${step.id}' completed by navigation to ${url}`);
    this.minimized.set(false);
    void this.next();
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), Math.max(min, max));
}
